import {
  ForemanEmployeeDispatch,
  ForemanEquipmentDispatch,
  ForemanJobDetail,
  ForemanPhase,
} from '@ups/xplat/api/dto';

function applyRemoteDefaultWorkOrderFields<
  T extends Pick<
    ForemanEmployeeDispatch,
    | 'foremanPhaseIDs'
    | 'foremanPhaseNames'
    | 'foremanPhaseID'
    | 'foremanPhaseName'
  >,
>(
  cached: T,
  remote: T | undefined,
  workOrders: ForemanPhase[] | undefined,
  keepLocalPhases = false
): void {
  if (!remote) {
    return;
  }
  const remoteIds = (remote.foremanPhaseIDs || [])
    .map((x) => String(x ?? '').trim())
    .filter(Boolean);
  if (remoteIds.length === 0) {
    return;
  }

  // Review 109265: on the open job, a work order the foreman added on the device (no hours yet, so not on the
  // server) stays after the server's list, with its own name.
  const localIds = (cached.foremanPhaseIDs || []).map((x) =>
    String(x ?? '').trim()
  );
  const kept = keepLocalPhases
    ? localIds
        .map((id, i) => ({ id, name: cached.foremanPhaseNames?.[i] }))
        .filter((p) => p.id && !remoteIds.includes(p.id))
    : [];
  cached.foremanPhaseIDs = [...remoteIds, ...kept.map((p) => p.id)];
  const remoteNames = remote.foremanPhaseNames || [];
  if (Array.isArray(remoteNames) && remoteNames.length === remoteIds.length) {
    cached.foremanPhaseNames = [
      ...remoteNames,
      ...kept.map((p) => p.name || p.id),
    ];
  } else {
    const wos = workOrders || [];
    cached.foremanPhaseNames = cached.foremanPhaseIDs.map((pid) => {
      const wo = wos.find(
        (w) =>
          String(w.foremanPhaseID ?? '').trim() === String(pid ?? '').trim()
      );
      return (wo?.phaseName || pid) as string;
    });
  }

  if (remote.foremanPhaseID != null && String(remote.foremanPhaseID).trim()) {
    cached.foremanPhaseID = remote.foremanPhaseID;
  }
  if (
    remote.foremanPhaseName != null &&
    String(remote.foremanPhaseName).trim()
  ) {
    cached.foremanPhaseName = remote.foremanPhaseName;
  }
}

/**
 * `mergeObjectsCachedPriorityMut` never overwrites cached array fields when the
 * key already exists, so stale `foremanPhaseIDs` survive a job-list refresh even
 * when the API returns the updated defaults. Copy explicit remote default WO
 * fields onto the merged (cached-wins) job when the server sends a non-empty
 * `foremanPhaseIDs` list for that row.
 */
export function reconcileDefaultWorkOrdersFromRemoteJob(
  mergedJob: ForemanJobDetail,
  remoteJob: ForemanJobDetail,
  keepLocalPhases = false
): void {
  const remoteEmpById = new Map(
    (remoteJob.employees || []).map(
      (e) => [String(e.foremanEmployeeJobID || ''), e] as const
    )
  );
  for (const emp of mergedJob.employees || []) {
    const id = String(emp.foremanEmployeeJobID || '');
    applyRemoteDefaultWorkOrderFields(
      emp,
      remoteEmpById.get(id),
      mergedJob.workOrders ?? remoteJob.workOrders,
      keepLocalPhases
    );
  }

  const remoteEqById = new Map(
    (remoteJob.equipment || []).map(
      (eq) =>
        [
          String(eq.foremanEquipmentDispatchID ?? eq.equipmentDispatchID ?? ''),
          eq,
        ] as const
    )
  );
  for (const eq of mergedJob.equipment || []) {
    const id = String(
      eq.foremanEquipmentDispatchID ?? eq.equipmentDispatchID ?? ''
    );
    applyRemoteDefaultWorkOrderFields(
      eq as ForemanEquipmentDispatch,
      remoteEqById.get(id) as ForemanEquipmentDispatch | undefined,
      mergedJob.workOrders ?? remoteJob.workOrders,
      keepLocalPhases
    );
  }
}
